import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  Currency,
  DateTime,
  ExportDropdown,
  Icon,
  IconButton,
  InternalClientTooltip,
  Percent,
  PeriodFilter,
  ProjectLink,
  SingleSelect,
  Tooltip,
  WorkspaceCurrencySelect,
  YesNoFilter,
} from '~/components';
import { CurrencyFilter } from '~/components/filters';
import AllocationBillableTypeFilter from '~/components/filters/AllocationBillableTypeFilter';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import FiltersGroup from '~/components/filters/FiltersGroup';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import ResourceFiltersBar from '~/components/filters/ResourceFiltersBar';
import ResourceFiltersGroup from '~/components/filters/ResourceFiltersGroup';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import AllocationBookingTypeFilter from '~/components/filters/AllocationBookingTypeFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, usePlaceholderFilters, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import projectStatuses from '~/lookups/project-statuses.js';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import List from '../components/List';
import ListTooltip from '../components/ListTooltip';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import { intervals, intervalsByScope } from './intervals';

const ProjectInfo = styled.div`
  margin-left: 2rem;
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Member = styled.div`
  margin-left: 1rem;
  display: flex;
  align-items: center;
  font-weight: ${weights.bold};
`;

export default function ServicesRevenuePlanByResourceAndProjectAndTimeUnit({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const features = useFeatures();

  const metricOptions = useMemo(() => {
    const options = {
      services_revenue: 'Services Revenue',
      services_profit: 'Services Gross Profit',
      labor_cost: 'Services Cost',
      services_margin: 'Services Gross Margin',
    };

    return options;
  }, []);

  const searchParamsConfig = useSearchParamsConfig();

  const [query, setQuery] = useState({ report: null, status: 'loading', loading: { table: false } });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters(() => ({
    projectRecordStatusId: {
      ...searchParamsConfig.projectRecordStatusId,
      default: 'active',
    },
    projectStatuses: {
      ...searchParamsConfig.projectStatuses,
      default: [projectStatuses.not_started, projectStatuses.in_progress],
    },
  }));
  const memberFilters = useMemberFilters();
  const placeholderFilters = usePlaceholderFilters();

  const [params, setParams] = useState({
    period: null,
    unit: 'month',
    currency: workspace.currency,
    metric: 'services_revenue',
    resourceTypeId: null,
    allocationBookingTypeId: null,

    ...memberFilters.filters,
    ...placeholderFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.next_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.next_30_days,
            week: intervals.next_12_weeks,
            month: intervals.next_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },
        currency: searchParamsConfig.currency,
        metric: { default: 'services_revenue', valid: _.keys(metricOptions) },
        resourceTypeId: searchParamsConfig.resourceTypeId,
        allocationBookingTypeId: searchParamsConfig.allocationBookingTypeId,

        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...placeholderFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters, memberFilters, placeholderFilters, metricOptions],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      currency: params.currency ?? undefined,
      resourceTypeId: params.resourceTypeId ?? undefined,
      allocationBookingTypeId: params.allocationBookingTypeId || undefined,

      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...placeholderFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, memberFilters, projectFilters, placeholderFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready', loading: { table: false } }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const [settingsVisible, setSettingsVisible] = useState(false);
  const showSettings = () => setSettingsVisible(true);
  const hideSettings = () => setSettingsVisible(false);
  const handleApplySettings = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideSettings();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="cog" onClick={showSettings} />

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.next_30_days,
                week: intervals.next_12_weeks,
                month: intervals.next_6_months,
              }[value],
            })
          }
        />

        {features.multicurrency && (
          <CurrencyFilter
            value={params.currency}
            onChange={({ target: { value } }) => handleApplySettings({ currency: value })}
          />
        )}

        <SingleSelectFilter
          icon="gear"
          placeholder="Metric"
          renderValue={(value) => value.name}
          value={params.metric}
          options={_.map(metricOptions, (value, key) => ({ id: key, name: value }))}
          onChange={({ target: { value } }) => handleApplySettings({ metric: value })}
          clearable={false}
        />

        <AllocationBookingTypeFilter
          value={params.allocationBookingTypeId}
          onChange={({ target: { value } }) => handleApplyFilters({ allocationBookingTypeId: value })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <ResourceFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Settings
                  values={params}
                  isOpen={settingsVisible}
                  onApply={handleApplySettings}
                  onClose={hideSettings}
                />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { resourceTypesById, resourcesById, projectsById, performancePeriods, periods } = useMemo(() => {
    const resourceTypesById = {
      member: { id: 'member', name: 'Members' },
      placeholder: { id: 'placeholder', name: 'Placeholders' },
    };

    const resourcesById = _.keyBy(report.resources, 'id');

    const projectsById = _.keyBy(report.projects, 'id');

    const performancePeriods = _.orderBy(report.periods, (p) => [
      p.resourceTypeId,
      resourcesById[p.resourceId]?.name?.toLowerCase(),
      projectsById[p.projectId]?.name?.toLowerCase(),
    ]).reduce(
      (a, v) => {
        a.total = a.total || {
          servicesRevenue: 0,
          laborCost: 0,
          servicesProfit: 0,
          get servicesMargin() {
            return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : 0;
          },
        };

        a.total.servicesRevenue += v.servicesRevenue;
        a.total.laborCost += v.laborCost;
        a.total.servicesProfit += v.servicesProfit;

        const resourceType = resourceTypesById[v.resourceTypeId];
        a.resourceTypes[resourceType.id] = a.resourceTypes[resourceType.id] || {
          total: {
            resourceTypeId: v.resourceTypeId,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
          },
          periods: {},
          resources: {},
        };
        // Resource Overall Totals
        a.resourceTypes[v.resourceTypeId].total.servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].total.laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].total.servicesProfit += v.servicesProfit;

        // Resource Type Periods
        if (!a.resourceTypes[v.resourceTypeId].periods[v.start]) {
          a.resourceTypes[v.resourceTypeId].periods[v.start] = {
            resourceTypeId: v.resourceTypeId,
            start: v.start,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
          };
        }
        a.resourceTypes[v.resourceTypeId].periods[v.start].servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].periods[v.start].laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].periods[v.start].servicesProfit += v.servicesProfit;

        a.resourceTypes[v.resourceTypeId].resources[v.resourceId] = a.resourceTypes[v.resourceTypeId].resources[
          v.resourceId
        ] || {
          periods: {},
          projects: {},
          total: {
            resourceTypeId: v.resourceTypeId,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : 0;
            },
          },
        };

        // Resource Overall Totals
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.servicesProfit += v.servicesProfit;

        // Resource Period Totals (accumulate across all projects for this resource+period)
        if (!a.resourceTypes[v.resourceTypeId].resources[v.resourceId].periods[v.start]) {
          a.resourceTypes[v.resourceTypeId].resources[v.resourceId].periods[v.start] = {
            resourceTypeId: v.resourceTypeId,
            resourceId: v.resourceId,
            start: v.start,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
          };
        }
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].periods[v.start].servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].periods[v.start].laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].periods[v.start].servicesProfit += v.servicesProfit;

        // Projects
        if (v.projectId) {
          a.resourceTypes[v.resourceTypeId].resources[v.resourceId].projects[v.projectId] = a.resourceTypes[
            v.resourceTypeId
          ].resources[v.resourceId].projects[v.projectId] || {
            periods: {},
            total: {
              servicesRevenue: 0,
              laborCost: 0,
              servicesProfit: 0,
              get servicesMargin() {
                return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
              },
            },
          };
          a.resourceTypes[v.resourceTypeId].resources[v.resourceId].projects[v.projectId].total.servicesRevenue +=
            v.servicesRevenue;
          a.resourceTypes[v.resourceTypeId].resources[v.resourceId].projects[v.projectId].total.laborCost +=
            v.laborCost;
          a.resourceTypes[v.resourceTypeId].resources[v.resourceId].projects[v.projectId].total.servicesProfit +=
            v.servicesProfit;

          a.resourceTypes[v.resourceTypeId].resources[v.resourceId].projects[v.projectId].periods[v.start] = {
            ...v,
          };
        }

        // Period Totals
        if (!a.periods[v.start]) {
          a.periods[v.start] = {
            start: v.start,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
          };
        }
        a.periods[v.start].servicesRevenue += v.servicesRevenue;
        a.periods[v.start].laborCost += v.laborCost;
        a.periods[v.start].servicesProfit += v.servicesProfit;

        return a;
      },
      { total: null, resourceTypes: {}, periods: {} },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { resourceTypesById, resourcesById, projectsById, performancePeriods, periods };
  }, [report, params.period.start, params.period.end, params.unit]);

  const metric = params.metric;

  return (
    <>
      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column minWidth="16rem" sticky>
              Resource
            </Table.Column>

            {periods.map((p) => {
              return (
                <Table.Column key={p} align="right" width="10rem">
                  <DateTime value={p} />
                </Table.Column>
              );
            })}

            <Table.Column align="right" width="10rem">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.loading.table}>
            {_.map(performancePeriods.resourceTypes, (resourceTypePerformance, resourceTypeId) => {
              const resourceType = resourceTypesById[resourceTypeId];

              return (
                <React.Fragment key={resourceTypeId}>
                  <Table.Row style={{ fontWeight: weights.bold }}>
                    <Table.Cell>
                      {resourceType.name} ({Object.keys(resourceTypePerformance.resources).length})
                    </Table.Cell>

                    {periods.map((period) => {
                      return (
                        <Table.Cell key={period}>
                          <PerformanceCell
                            value={resourceTypePerformance.periods[period]}
                            metric={metric}
                            currency={report.currency}
                          />
                        </Table.Cell>
                      );
                    })}

                    <Table.Cell>
                      <PerformanceCell
                        value={resourceTypePerformance.total}
                        metric={metric}
                        currency={report.currency}
                      />
                    </Table.Cell>
                  </Table.Row>

                  {_.map(resourceTypePerformance.resources, (resourcePerformance, resourceId) => {
                    const resource = resourcesById[resourceId];

                    return (
                      <React.Fragment key={resourceId}>
                        <Table.GroupRow key={resourceId} top="3rem">
                          <Table.Cell>
                            <Member>
                              {resource.name}
                              {{
                                member: () => (
                                  <Tooltip message="Member">
                                    <Icon icon="user" color={colors.grey25} spaceLeft />
                                  </Tooltip>
                                ),
                                placeholder: () => (
                                  <Tooltip message="Resource Placeholder">
                                    <Icon icon="users" color={colors.grey25} spaceLeft />
                                  </Tooltip>
                                ),
                              }[resource.resourceTypeId]()}
                            </Member>
                          </Table.Cell>

                          {periods.map((period) => {
                            return (
                              <Table.Cell key={period}>
                                <PerformanceCell
                                  value={resourcePerformance.periods[period]}
                                  metric={metric}
                                  currency={report.currency}
                                />
                              </Table.Cell>
                            );
                          })}

                          <Table.Cell>
                            <PerformanceCell
                              value={resourcePerformance.total}
                              metric={metric}
                              currency={report.currency}
                            />
                          </Table.Cell>
                        </Table.GroupRow>

                        {_.map(resourcePerformance.projects, (projectPerformance, projectId) => {
                          const project = projectsById[projectId];

                          return (
                            <Table.Row key={projectId}>
                              <Table.Cell>
                                <ProjectInfo>
                                  <p>
                                    <ProjectLink project={project} />
                                  </p>
                                  <small>
                                    <ClientLink client={project.client} />
                                    {project.client.isInternal && <InternalClientTooltip />}
                                  </small>
                                </ProjectInfo>
                              </Table.Cell>

                              {periods.map((period) => {
                                return (
                                  <Table.Cell key={period}>
                                    <PerformanceCell
                                      value={projectPerformance.periods?.[period]}
                                      metric={metric}
                                      currency={report.currency}
                                    />
                                  </Table.Cell>
                                );
                              })}

                              <Table.Cell>
                                <PerformanceCell
                                  value={projectPerformance.total}
                                  metric={metric}
                                  currency={report.currency}
                                />
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>

              {periods.map((period) => {
                return (
                  <Table.Cell key={period}>
                    <PerformanceCell
                      value={performancePeriods.periods[period]}
                      metric={metric}
                      currency={report.currency}
                    />
                  </Table.Cell>
                );
              })}

              <Table.Cell>
                <PerformanceCell value={performancePeriods.total} metric={metric} currency={report.currency} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status total={_.size(resourcesById)} label="Resource" isLoading={query.status !== 'ready'} />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.next_30_days,
              week: intervals.next_12_weeks,
              month: intervals.next_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <YesNoFilter
        icon="filter"
        placeholder="Only show resources with allocations"
        value={filters.onlyAllocatedResources ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ onlyAllocatedResources: value === 'yes' })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <ResourceFiltersGroup filters={filters} onChange={handleFilter} />

      <FiltersGroup
        label="Allocation Filters"
        filters={[filters.allocationBookingTypeId, filters.allocationBillableTypes]}>
        <AllocationBookingTypeFilter
          value={filters.allocationBookingTypeId}
          onChange={({ target: { value } }) => handleFilter({ allocationBookingTypeId: value })}
        />

        <AllocationBillableTypeFilter
          value={filters.allocationBillableTypes}
          onChange={({ target: { value } }) => handleFilter({ allocationBillableTypes: value })}
        />
      </FiltersGroup>
    </Report.FiltersDrawer>
  );
}

function Settings({ values, isOpen, onClose, onApply }) {
  const features = useFeatures();
  const [settings, setSettings] = useState(values);

  const handleApply = () => {
    onApply(settings);
  };

  const handleChange = (filter) => {
    setSettings({ ...settings, ...filter });
  };

  const handleCancel = () => {
    setSettings(values);
    onClose();
  };

  useEffect(() => {
    setSettings(values);
  }, [values]);

  const metricOptions = useMemo(() => {
    const options = {
      services_revenue: 'Services Revenue',
      services_profit: 'Services Gross Profit',
      labor_cost: 'Services Cost',
      services_margin: 'Services Gross Margin',
    };

    return options;
  }, []);

  return (
    <Report.FiltersDrawer title="Settings" isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <SingleSelect
        materialPlaceholder="Metric"
        value={settings.metric}
        onChange={({ target: { value } }) => handleChange({ metric: value })}>
        {_.map(metricOptions, (value, key) => (
          <option key={key} value={key}>
            {value}
          </option>
        ))}
      </SingleSelect>

      {features.multicurrency && (
        <WorkspaceCurrencySelect
          clearable={false}
          value={settings.currency}
          onChange={({ target: { value } }) => handleChange({ currency: value })}
        />
      )}
    </Report.FiltersDrawer>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}

function PerformanceCell({ value, metric, currency }) {
  return (
    <PerformanceTooltip value={value} currency={currency}>
      <div>
        <PerformanceValue value={value} metric={metric} currency={currency} />
      </div>
    </PerformanceTooltip>
  );
}

function PerformanceValue({ value, metric, currency }) {
  switch (metric) {
    case 'services_revenue':
      return <Currency value={value.servicesRevenue} currency={currency} />;

    case 'labor_cost':
      return <Currency value={value.laborCost} currency={currency} />;

    case 'services_profit':
      return <Currency value={value.servicesProfit} currency={currency} />;

    case 'services_margin':
      return <Percent value={value.servicesMargin} />;

    default:
      return null;
  }
}

function PerformanceTooltip({ value, currency, children }) {
  if (!value) return '-';

  return (
    <ListTooltip
      message={
        <List>
          <List.Item label="Services Revenue">
            <Currency value={value.servicesRevenue} currency={currency} />
          </List.Item>
          <List.Item label="Services Cost">
            <Currency value={value.laborCost} currency={currency} />
          </List.Item>
          <List.Item label="Services Gross Profit">
            <Currency value={value.servicesProfit} currency={currency} />
          </List.Item>
          <List.Item label="Services Gross Margin">
            <Percent value={value.servicesMargin} />
          </List.Item>
        </List>
      }>
      {children}
    </ListTooltip>
  );
}
